import { onMounted, onUnmounted, type Ref, useTemplateRef, watch } from 'vue';
import { gsap } from 'gsap';
import { CSSPlugin } from 'gsap/CSSPlugin';
import type { ChatBgShapeConfig } from '@/models/chat';

export const useChatBgShape = (isChameleonMode: Ref<boolean>, config: ChatBgShapeConfig) => {
  gsap.registerPlugin(CSSPlugin);

  const svgRef = useTemplateRef<SVGElement>('svgRef');
  const defaultTimeline = ref<gsap.core.Timeline>();
  const chameleonTimeline = ref<gsap.core.Timeline>();

  const initDefault = () => {
    if (!svgRef.value || defaultTimeline.value) return;

    defaultTimeline.value = gsap.timeline().to(svgRef.value, config.initialStart).to(svgRef.value, config.initialEnd);
  };

  const pauseDefault = () => {
    defaultTimeline.value?.pause();
  };

  const resumeDefault = () => {
    if (!defaultTimeline.value) initDefault();

    defaultTimeline.value?.invalidate()?.restart();
  };

  const initChameleon = () => {
    if (!svgRef.value || chameleonTimeline.value) return;

    chameleonTimeline.value = gsap.timeline().to(svgRef.value, config.chameleonStart).to(svgRef.value, config.chameleonEnd);
  };

  const pauseChameleon = () => {
    chameleonTimeline.value?.pause();
  };

  const resumeChameleon = () => {
    if (!chameleonTimeline.value) initChameleon();

    chameleonTimeline.value?.invalidate()?.restart();
  };

  onMounted(() => {
    initDefault();
  });

  onUnmounted(() => {
    defaultTimeline.value?.kill();
    defaultTimeline.value = undefined;

    chameleonTimeline.value?.kill();
    chameleonTimeline.value = undefined;
  });

  watch(isChameleonMode, (chameleonMode) => {
    if (chameleonMode) {
      pauseDefault();
      resumeChameleon();
    } else {
      pauseChameleon();
      resumeDefault();
    }
  });

  return {
    svgRef
  };
};
